import { useState, type FormEvent } from 'react';
import { api, errorText } from '../../api';
import { fieldId, SelectField, TextField, useForm, type FormErrors } from '../../auth/CompanyFields';
import { t, tl } from '../../i18n';
import { fmtDate, today } from '../../lib/format';
import { useToast } from '../../lib/toast';
import { Upload } from './Questionnaire';
import type { EmployeeDetail } from './types';

// „Änderung melden“ und „Kündigung melden“ in der Personalakte (wie in der Demo). Beides geht an die
// Lohnbuchhaltung; die Prüfungen sind dieselben wie auf dem Server.

// Werte wie auf dem Server (Deutsch); angezeigt werden sie übersetzt
const CHANGE_WHAT = [
  tl('Adresse'),
  tl('Bankverbindung'),
  tl('Gehalt / Stundenlohn'),
  tl('Arbeitszeit'),
  tl('Steuerklasse / Kinder'),
  tl('Krankenkasse'),
  tl('Name'),
  tl('Elternzeit / Mutterschutz'),
  tl('Krank länger als 6 Wochen'),
  tl('Sonstiges'),
];
const TERM_BY = [tl('Arbeitgeber'), tl('Arbeitnehmer'), tl('Aufhebungsvertrag'), tl('Befristung läuft aus')];
// Kündigung aus einer übernommenen Mitarbeiterliste der Kanzlei (Server: TERM_IMPORT), wird nur angezeigt
export const TERM_IMPORT = tl('Aus der Mitarbeiterliste übernommen');

const needsDate = (what: string) => /^(Gehalt|Arbeitszeit|Steuerklasse|Krankenkasse|Elternzeit|Krank)/.test(what);
// Ändert sich der Arbeitsvertrag, muss der Arbeitgeber den Mitarbeiter schriftlich informieren (Gesetzes-Prüfung C2)
const changesContract = (what: string) => /^(Gehalt|Arbeitszeit|Sonstiges)/.test(what);
const dateLabel = (what: string) => (/^(Elternzeit|Krank)/.test(what) ? tl('Beginn') : tl('Gültig ab'));
const dateOk = (v: string) =>
  /^\d{4}-\d{2}-\d{2}$/.test(v) && !Number.isNaN(Date.parse(v)) && v >= '2000-01-01' && v <= '2100-12-31';
const isoOf = (d: Date) => d.toISOString().slice(0, 10);
const todayIso = () => {
  const d = today();
  return isoOf(new Date(Date.UTC(d.year, d.month - 1, d.day)));
};

// Fehler am Feld zeigen, das erste Feld in den Blick holen und die Meldung auch unten anzeigen
function showErrors(f: { setErrors(e: FormErrors): void }, errors: FormErrors, order: string[], toast: (m: string) => void) {
  const first = order.find((k) => errors[k]);
  if (!first) return false;
  f.setErrors(errors);
  const el = document.getElementById(fieldId(first));
  el?.focus();
  el?.scrollIntoView({ block: 'center' });
  toast(errors[first]!);
  return true;
}

function post(url: string, data: unknown, field: string, file: File | undefined) {
  // zuerst die Angaben, dann die Datei (so prüft der Server, bevor er speichert)
  const fd = new FormData();
  fd.append('data', JSON.stringify(data));
  if (file) fd.append(field, file, file.name);
  return api.post(url, fd);
}

export function ChangeForm({ e, onDone, onCancel }: { e: EmployeeDetail; onDone(): void; onCancel(): void }) {
  const toast = useToast();
  const f = useForm({});
  const [file, setFile] = useState<File>();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const what = f.values.what ?? '';
  const need = needsDate(what);

  async function submit(ev: FormEvent) {
    ev.preventDefault();
    setError(null);
    const v = (k: string) => (f.values[k] ?? '').trim();
    const errors: FormErrors = {};
    if (!what) errors.what = t('Bitte auswählen, was sich ändert.');
    if (!v('value')) errors.value = t('Bitte die neuen Angaben eintragen.');
    if (v('validFrom') && !dateOk(v('validFrom'))) errors.validFrom = t('Bitte das Datum prüfen.');
    else if (need && !v('validFrom')) errors.validFrom = t('Bitte „{label}“ angeben.', { label: t(dateLabel(what)) });
    if (showErrors(f, errors, ['what', 'value', 'validFrom'], toast)) return;
    setBusy(true);
    try {
      await post(
        `/api/app/employee/${e.id}/changes`,
        { what, value: v('value'), validFrom: v('validFrom') || null },
        'nachweis',
        file,
      );
      toast(t('Änderung an Ihre Lohnbuchhaltung gesendet'));
      onDone();
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  const placeholder = /^Elternzeit/.test(what)
    ? t('z. B. Elternzeit bis 31.12.2027')
    : /^Krank/.test(what)
      ? t('z. B. krankgeschrieben seit 20.08.2026')
      : t('z. B. neue IBAN, neue Adresse, 3.200 € brutto');
  return (
    <form className="form maform mform" onSubmit={submit} noValidate>
      <SelectField
        f={f}
        name="what"
        label={t('Was ändert sich?')}
        required
        options={CHANGE_WHAT}
        placeholder={t('Bitte wählen')}
      />
      <TextField
        f={f}
        name="value"
        label={t('Neue Angaben')}
        required
        maxLength={300}
        placeholder={placeholder}
        autoComplete="off"
      />
      <div className="two">
        <TextField
          f={f}
          name="validFrom"
          label={t(dateLabel(what))}
          required={need}
          type="date"
          min="2000-01-01"
          max="2100-12-31"
        />
        <Upload k="nachweis" label={t('Nachweis (optional)')} required={false} file={file} error={undefined} onFile={setFile} />
      </div>
      {changesContract(what) && (
        <small className="fh">
          {dateOk(f.values.validFrom ?? '')
            ? t(
                'Ändert sich damit der Arbeitsvertrag, informieren Sie den Mitarbeiter bitte spätestens am {date} schriftlich, am besten mit einer Vertragsänderung, die Sie beide unterschreiben. Steigt nur der Mindestlohn oder der Tariflohn, ist das nicht nötig.',
                { date: fmtDate(f.values.validFrom!) },
              )
            : t(
                'Ändert sich damit der Arbeitsvertrag, informieren Sie den Mitarbeiter bitte spätestens am Tag der Änderung schriftlich, am besten mit einer Vertragsänderung, die Sie beide unterschreiben. Steigt nur der Mindestlohn oder der Tariflohn, ist das nicht nötig.',
              )}
        </small>
      )}
      {error && (
        <p className="formerr" role="alert">
          {error}
        </p>
      )}
      <div className="row">
        <button className="btn2 dark" type="submit" disabled={busy}>
          {busy ? t('Einen Moment …') : t('An Lohnbuchhaltung senden')}
        </button>
        <button className="btn2" type="button" onClick={onCancel}>
          {t('Abbrechen')}
        </button>
      </div>
      <div className="src">
        {t('Ihre Lohnbuchhaltung übernimmt die Änderung ab dem angegebenen Datum, sonst ab der nächsten Abrechnung.')}
      </div>
    </form>
  );
}

// Hinweis unter den Daten: nachträgliche Meldung oder kürzer als die gesetzliche Grundfrist (§ 622 BGB)
function terminationHint(by: string, got: string, last: string, now: string): string {
  if (dateOk(last) && last < now)
    return t('Nachträgliche Meldung: Der letzte Arbeitstag ist schon vorbei. Bitte schnell senden.');
  if ((by === 'Arbeitgeber' || by === 'Arbeitnehmer') && dateOk(got) && dateOk(last)) {
    const g = new Date(`${got}T00:00:00Z`);
    g.setUTCDate(g.getUTCDate() + 28);
    const earliest =
      g.getUTCDate() <= 15
        ? new Date(Date.UTC(g.getUTCFullYear(), g.getUTCMonth(), 15))
        : new Date(Date.UTC(g.getUTCFullYear(), g.getUTCMonth() + 1, 0));
    if (last < isoOf(earliest)) {
      return t(
        'Hinweis: Die gesetzliche Grundfrist ist 4 Wochen zum 15. oder zum Monatsende, hier also frühestens zum {date}. Bei fristloser Kündigung oder in der Probezeit kann es kürzer sein, Ihre Kanzlei prüft das.',
        { date: fmtDate(isoOf(earliest)) },
      );
    }
  }
  return '';
}

export function TerminationForm({ e, onDone, onCancel }: { e: EmployeeDetail; onDone(): void; onCancel(): void }) {
  const toast = useToast();
  const f = useForm({});
  const [file, setFile] = useState<File>();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const now = todayIso();
  const v = (k: string) => (f.values[k] ?? '').trim();
  const by = v('by');
  const befristung = by === 'Befristung läuft aus';
  const aufhebung = by === 'Aufhebungsvertrag';
  const hint = terminationHint(by, v('receivedOn'), v('lastDay'), now);

  async function submit(ev: FormEvent) {
    ev.preventDefault();
    setError(null);
    const got = befristung ? '' : v('receivedOn');
    const last = v('lastDay');
    const urlaub = v('vacationDays').replace(',', '.');
    const errors: FormErrors = {};
    if (!by) errors.by = t('Bitte auswählen, wer gekündigt hat.');
    if (!befristung && by && !got) {
      errors.receivedOn = aufhebung
        ? t('Bitte angeben, wann der Aufhebungsvertrag unterschrieben wurde.')
        : t('Bitte angeben, wann die Kündigung zugegangen ist.');
    } else if (got && !dateOk(got)) errors.receivedOn = t('Bitte das Datum prüfen.');
    if (!dateOk(last)) errors.lastDay = t('Bitte den letzten Arbeitstag prüfen.');
    else if (last < e.entryDate) {
      errors.lastDay = t('Der letzte Arbeitstag liegt vor dem Eintritt am {date}.', { date: fmtDate(e.entryDate) });
    } else if (got && dateOk(got) && got > last) {
      errors.receivedOn = aufhebung
        ? t('Der Vertrag liegt nach dem letzten Arbeitstag. Bitte die Daten prüfen.')
        : t('Die Kündigung liegt nach dem letzten Arbeitstag. Bitte die Daten prüfen.');
    }
    if (urlaub) {
      const n = Number(urlaub);
      if (!Number.isFinite(n) || n < 0) errors.vacationDays = t('Bitte beim Resturlaub eine Zahl ab 0 eingeben.');
      else if (n > 365) errors.vacationDays = t('Bitte den Resturlaub prüfen.');
      else if (!Number.isInteger(n * 2)) errors.vacationDays = t('Resturlaub bitte in ganzen oder halben Tagen angeben.');
    }
    if (showErrors(f, errors, ['by', 'receivedOn', 'lastDay', 'vacationDays'], toast)) return;
    setBusy(true);
    try {
      await post(
        `/api/app/employee/${e.id}/termination`,
        { by, receivedOn: got || null, lastDay: last, vacationDays: urlaub ? Number(urlaub) : null, note: v('note') || null },
        'schreiben',
        file,
      );
      toast(last < now ? t('Nachträgliche Kündigung an Ihre Kanzlei gesendet') : t('Kündigung an Ihre Kanzlei gesendet'));
      onDone();
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  return (
    <form className="form maform mform" onSubmit={submit} noValidate>
      <SelectField f={f} name="by" label={t('Wer hat gekündigt?')} required options={TERM_BY} placeholder={t('Bitte wählen')} />
      <div className="two">
        {!befristung && (
          <TextField
            f={f}
            name="receivedOn"
            label={aufhebung ? t('Vertrag unterschrieben am') : t('Kündigung zugegangen am')}
            required={!!by}
            type="date"
            min="2000-01-01"
            max="2100-12-31"
          />
        )}
        <TextField f={f} name="lastDay" label={t('Letzter Arbeitstag')} required type="date" min={e.entryDate} max="2100-12-31" />
      </div>
      {hint && <small className="fh warn">{hint}</small>}
      <div className="two">
        <TextField
          f={f}
          name="vacationDays"
          label={t('Resturlaub (Tage)')}
          type="number"
          min={0}
          step={0.5}
          inputMode="decimal"
        />
        <Upload
          k="schreiben"
          label={t('Kündigungsschreiben (optional)')}
          required={false}
          file={file}
          error={undefined}
          onFile={setFile}
        />
      </div>
      <TextField
        f={f}
        name="note"
        label={t('Hinweis an die Kanzlei (optional)')}
        maxLength={300}
        placeholder={t('z. B. Abfindung, Freistellung, Firmenwagen zurück')}
        autoComplete="off"
      />
      {error && (
        <p className="formerr" role="alert">
          {error}
        </p>
      )}
      <div className="row">
        <button className="btn2 dark" type="submit" disabled={busy}>
          {busy ? t('Einen Moment …') : t('Kündigung an Kanzlei senden')}
        </button>
        <button className="btn2" type="button" onClick={onCancel}>
          {t('Abbrechen')}
        </button>
      </div>
      <div className="src">
        {t(
          'Ihre Kanzlei meldet den Mitarbeiter bei der Krankenkasse ab, erstellt die letzte Lohnabrechnung und die Arbeitsbescheinigung.',
        )}
      </div>
    </form>
  );
}
